import type {
  ChargeType,
  Currency,
  QuoteDetailPublic,
  QuoteDispositionKind,
  QuoteTaskChargePublic,
  QuoteTaskDetailPublic,
  QuoteTaskStatus,
} from "@common/client"
import { toNumberOrNull } from "@common/lib/number"
import {
  chargeAmount,
  formatPrice,
  formatTotals,
} from "@/routes/_layout/quoting_/quotes/-lib/charge-math"
import {
  partCharges,
  partsRollup,
  totalCharges,
} from "@/routes/_layout/quoting_/quotes/builder/$quoteId/-common/lib/task-form"
import { labourCharge, labourDone } from "./labour"
import {
  highestOffer,
  isPartsQuote,
  WORKSCOPES,
  workscopesDone,
} from "./parts-quote"

export const TASK_STATUSES = [
  "draft",
  "ready",
  "ready_for_customer",
  "clarification_requested",
  "approved",
  "rejected",
  "excluded",
] as const satisfies readonly QuoteTaskStatus[]

export function taskStatusLabel(
  status: QuoteTaskStatus,
  disposition?: QuoteDispositionKind,
): string {
  switch (status) {
    case "draft":
      return "Draft"
    case "ready":
      return disposition === "review" ? "In review" : "Ready"
    case "ready_for_customer":
      switch (disposition) {
        case "review":
          return "Reviewed"
        case "customer_review":
          return "Sent"
        default:
          return "Ready for customer"
      }
    case "clarification_requested":
      return "Needs changes"
    case "approved":
      return "Approved"
    case "rejected":
      return "Rejected"
    case "excluded":
      return "Excluded"
  }
}

export function serviceCharges(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic[] {
  return task.active_charges.filter(
    (charge) => charge.type === "outside_service",
  )
}

export const OTHER_CHARGE_TYPES = [
  "misc",
  "freight",
  "insurance",
] as const satisfies readonly ChargeType[]

export type OtherChargeType = (typeof OTHER_CHARGE_TYPES)[number]

export function isOtherChargeType(type: string): type is OtherChargeType {
  return OTHER_CHARGE_TYPES.some((other) => other === type)
}

export function otherCharges(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic[] {
  return task.active_charges.filter((charge) => isOtherChargeType(charge.type))
}

function priced(charge: QuoteTaskChargePublic): boolean {
  return toNumberOrNull(charge.sale_unit_price) !== null
}

export type Sections = {
  labour: boolean
  parts: boolean
  services: boolean
  other: boolean
}

export function sectionsDone(task: QuoteTaskDetailPublic): Sections {
  const covered = partsRollup(task) !== null
  return {
    labour: labourDone(labourCharge(task)),
    parts: covered || partCharges(task).every(priced),
    services: serviceCharges(task).every(priced),
    other: otherCharges(task).every(priced),
  }
}

export function doneCount(sections: Sections): number {
  return Object.values(sections).filter(Boolean).length
}

export type Progress = { done: number; total: number }

export function progressOf(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): Progress {
  if (isPartsQuote(quote)) {
    return { done: workscopesDone(task), total: WORKSCOPES.length }
  }
  const sections = sectionsDone(task)
  return { done: doneCount(sections), total: Object.keys(sections).length }
}

export function isComplete(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): boolean {
  const { done, total } = progressOf(quote, task)
  return done === total
}

export function taskTotal(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
  fallback: Currency,
): string {
  if (isPartsQuote(quote)) {
    const highest = highestOffer(task)
    return highest === null ? "" : formatTotals([highest]).join(" · ")
  }
  return chargesPrice(totalCharges(task), fallback)
}

export function chargesPrice(
  charges: QuoteTaskChargePublic[],
  fallback: Currency,
): string {
  const hasAmount = charges.some((charge) => chargeAmount(charge) !== null)
  return hasAmount ? formatPrice(charges, fallback) : ""
}

export function pricedParts(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic[] {
  const rollup = partsRollup(task)
  return rollup === null ? partCharges(task) : [rollup]
}

export function lastUpdated(task: QuoteTaskDetailPublic): string {
  return task.active_charges.reduce(
    (latest, charge) =>
      charge.updated_at > latest ? charge.updated_at : latest,
    task.updated_at,
  )
}

export function acceptedStatus(
  disposition: QuoteDispositionKind,
): QuoteTaskStatus {
  return disposition === "draft" ? "ready" : "ready_for_customer"
}

export function includedStatus(
  disposition: QuoteDispositionKind,
): QuoteTaskStatus {
  switch (disposition) {
    case "draft":
      return "draft"
    case "review":
      return "ready"
    default:
      return "ready_for_customer"
  }
}

export function isAccepted(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): boolean {
  return (
    task.status === "approved" ||
    task.status === acceptedStatus(quote.disposition)
  )
}

const OPEN_STATUSES: QuoteTaskStatus[] = ["draft", "clarification_requested"]

export function nextToFinalise(
  tasks: QuoteTaskDetailPublic[],
  taskId: string,
): QuoteTaskDetailPublic | undefined {
  const index = tasks.findIndex((task) => task.id === taskId)
  const open = tasks.filter(
    (task) => task.id !== taskId && OPEN_STATUSES.includes(task.status),
  )
  return open.find((task) => tasks.indexOf(task) > index) ?? open[0]
}

export function neighbour(
  tasks: QuoteTaskDetailPublic[],
  taskId: string,
  step: number,
): QuoteTaskDetailPublic | undefined {
  const index = tasks.findIndex((task) => task.id === taskId)
  if (index === -1 || tasks.length < 2) return undefined
  return tasks[(index + step + tasks.length) % tasks.length]
}
